<template>
  <div class="social-login">
    <p class="social-login__label">Or continue with</p>
    <div class="social-login__buttons">
      <button
        class="social-btn social-btn--google"
        type="button"
        :disabled="loading"
        @click="handleLogin('google')"
      >
        <!-- Google 'G' logo -->
        <svg class="social-btn__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      <button
        class="social-btn social-btn--microsoft"
        type="button"
        :disabled="loading"
        @click="handleLogin('microsoft')"
      >
        <!-- Microsoft four-square logo (covers Outlook and Hotmail accounts) -->
        <svg class="social-btn__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="1"  y="1"  width="10.5" height="10.5" fill="#F25022"/>
          <rect x="12.5" y="1"  width="10.5" height="10.5" fill="#7FBA00"/>
          <rect x="1"  y="12.5" width="10.5" height="10.5" fill="#00A4EF"/>
          <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900"/>
        </svg>
        Continue with Microsoft
      </button>

      <button
        class="social-btn social-btn--github"
        type="button"
        :disabled="loading"
        @click="handleLogin('github')"
      >
        <!-- GitHub logo -->
        <svg class="social-btn__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.004 12.004 0 0024 12c0-6.63-5.37-12-12-12z" fill="#181717"/>
        </svg>
        Continue with GitHub
      </button>
    </div>

    <p v-if="error" class="social-login__error">{{ error }}</p>
  </div>
</template>

<script>
import { startSocialLogin } from '@/assets/js/auth/oauth-service';

export default {
  name: 'SocialLoginButtons',
  data() {
    return {
      loading: false,
      error: '',
    };
  },
  methods: {
    async handleLogin(provider) {
      this.error = '';
      this.loading = true;
      try {
        // startSocialLogin redirects the browser; it only returns on error
        await startSocialLogin(provider);
      } catch (err) {
        this.loading = false;
        if (err && err.code === 404) {
          this.error = 'This login provider is not configured on the server.';
        } else if (err && err.message) {
          this.error = err.message;
        } else {
          this.error = 'Could not start social login. Please try again.';
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.social-login {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;

  &__label {
    text-align: center;
    font-size: 14px;
    margin: 0;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__error {
    font-size: 14px;
    text-align: center;
    margin: 4px 0 0;
    @include themify() {
      color: themed('login-error');
    }
  }
}

.social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 44px;
  border-radius: 6px;
  border: 2px solid rgb(189, 186, 186);
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  letter-spacing: 0.02em;

  @include themify() {
    color: themed('body-text-color');
  }

  &:hover:not(:disabled) {
    @include themify() {
      background: themed('button-secondary-hover');
      border-color: themed('color-primary');
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &__icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
}
</style>
